<template>
  <v-container class="ma-0 px-0 white" fluid>
    <v-row class="fixed-header rounded-top ma-0 pl-6 pr-3 pt-3 pb-2">
      <v-col cols="4" class="ma-0 pa-0">
        <Menu :options="statusOptions">
          <template v-slot:activator="{ on, value }">
            <div class="d-flex align-center mt-1 cursor-pointer" v-on="on">
              <v-icon
                class="mr-2"
                size="18"
                :color="isCompleted ? 'green' : 'blue-grey darken-1'"
                @click.stop="handleStatusClick"
              >
                {{ statusIcon }}
              </v-icon>
              <span class="body-2 blue-grey--text text--darken-3">
                {{ statusText }}
              </span>
              <v-icon class="ml-2" size="18" color="blue-grey darken-1">
                {{ value ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </div>
          </template>
        </Menu>
      </v-col>
      <v-col class="d-flex justify-end align-center">
        <PlannerIntegrationBadge
          v-if="isPlannerBadgesEnabled"
          class="mr-4"
          :data="plannerIntegration"
          :tooltipMessage="$t('common.click_to_open_task_in_ms_planner')"
        />
        <v-tooltip v-if="!isEditMode" bottom>
          <template v-slot:activator="{ on }">
            <div class="mr-1 cursor-pointer bubble pa-2 d-flex justify-center">
              <v-icon
                v-on="on"
                size="20"
                color="blue-grey darken-1"
                @click="$emit('minimize')"
              >
                mdi-minus
              </v-icon>
            </div>
          </template>
          {{ $t('common.minimize') }}
        </v-tooltip>
        <v-tooltip v-if="isEditMode" bottom>
          <template v-slot:activator="{ on }">
            <div class="mr-1 cursor-pointer bubble pa-2 d-flex justify-center">
              <v-icon
                v-on="on"
                size="20"
                color="blue-grey darken-1"
                @click="$emit('duplicate')"
              >
                mdi-content-copy
              </v-icon>
            </div>
          </template>
          {{ $t('common.button.duplicate') }}
        </v-tooltip>
        <v-tooltip v-if="isEditMode" bottom>
          <template v-slot:activator="{ on }">
            <div class="mr-1 cursor-pointer bubble pa-2 d-flex justify-center">
              <v-icon
                v-on="on"
                size="20"
                color="blue-grey darken-1"
                @click="copyToClipboard"
              >
                mdi-link-variant
              </v-icon>
            </div>
          </template>
          {{ $t('common.copy_link') }}
        </v-tooltip>
        <v-tooltip v-if="isEditMode && permissions.delete" bottom>
          <template v-slot:activator="{ on }">
            <div class="mr-1 cursor-pointer bubble pa-2 d-flex justify-center">
              <v-icon
                v-on="on"
                size="20"
                color="blue-grey darken-1"
                @click="$emit('delete')"
              >
                mdi-delete-outline
              </v-icon>
            </div>
          </template>
          {{ $t('common.button.delete') }}
        </v-tooltip>
        <div class="cursor-pointer bubble pa-2 d-flex justify-center">
          <v-icon size="20" color="blue-grey darken-1" @click="$emit('close')">
            mdi-close
          </v-icon>
        </div>
      </v-col>
    </v-row>

    <div class="px-6 mt-10">
      <div class="ma-0 mb-2 pt-5">
        <div
          class="d-flex justify-space-between align-center"
          :class="{ 'mt-2': hasLinkedEntity || hasSubscribers }"
        >
          <div class="d-flex justify-center align-center">
            <v-icon size="16" color="blue-grey darken-1">
              {{ privacyIcon }}
            </v-icon>
            <span
              class="blue-grey--text text--darken-1 caption text-truncate ml-2 my-0"
              >{{ privacyText }}</span
            >
            <v-tooltip
              v-if="hasLinkedEntity || hasSubscribers"
              top
              max-width="270px"
            >
              <template v-slot:activator="{ on }">
                <v-icon
                  v-on="on"
                  class="ml-2"
                  size="16"
                  color="blue-grey darken-1"
                >
                  mdi-information-outline
                </v-icon>
              </template>
              {{ publicTaskReason }}
            </v-tooltip>
          </div>
          <Button
            v-if="!hasLinkedEntity && !hasSubscribers"
            plain
            :disabled="hasLinkedEntity"
            :text="privacyButtonText"
            @click="handleChange('isPrivate', !task.isPrivate)"
          />
        </div>
        <v-textarea
          auto-grow
          solo
          flat
          hide-details
          autofocus
          rows="1"
          max-rows="5"
          class="h6 py-0 ml-n2 mt-0"
          ref="taskTitle"
          :maxlength="$constants.TASK.nameMaxLength"
          :value="name"
          :placeholder="$t('tasks.new_task')"
          @input="handleChange('name', $event)"
        />
      </div>

      <div v-if="hasLinkedEntity" class="mb-4">
        <v-chip
          small
          color="blue-grey lighten-3"
          :class="{
            'entity-chip': !entityNotFound,
            'cursor-pointer': !entityNotFound,
          }"
          :ripple="!entityNotFound"
          @click="goToEntity"
        >
          <EntityIcon :entityType="task.entityType" class="mr-1" />
          <span
            class="caption text-truncate mr-2"
            :class="entityNotFound ? 'blue-grey--text' : 'blue--text'"
            >{{
              isLoadingEntity
                ? `${$t('common.loading')}...`
                : entityName || task.entityName
            }}</span
          >
        </v-chip>
      </div>

      <v-row no-gutters class="mb-4">
        <v-col cols="6">
          <p class="mb-1 pa-0 blue-grey--text caption">
            {{ $t('common.created_by') }}
          </p>
          <ChipAvatar
            disablePopover
            hideBorder
            :ripple="false"
            :account="createdBy"
          />
        </v-col>
        <v-col
          cols="6"
          class="d-flex flex-column flex-grow-1 justify-start"
          data-tourid="tasks-creator-assignee"
        >
          <div class="d-flex align-center mb-1">
            <p class="mb-0 pa-0 blue-grey--text caption">
              {{ $t('common.assignee_one') }}
            </p>
            <div v-if="isAssigneeOtherSite">
              <v-icon color="yellow" class="ml-2 mr-1" size="18"
                >mdi-alert-circle-outline
              </v-icon>
              <span class="caption yellow--text">{{
                $t('tasks.user_other_site')
              }}</span>
            </div>
          </div>
          <AssigneesSearchPopover
            :selectedAccountIds="assigneeId ? [assigneeId] : []"
            @change="handleAssigneesChange"
          >
            <ChipAvatar
              v-if="hasAssignee"
              disablePopover
              hideBorder
              :ripple="false"
              :account="assignee"
              :vuetifyProps="{ close: true }"
              @click:close="handleChange('assignees', [])"
            />
            <v-btn
              v-else
              rounded
              small
              class="blue-grey--text text--darken-1 body-2"
              color="blue-grey lighten-3"
              elevation="0"
              data-tourid="assignee-selector-trigger"
            >
              <v-icon size="16" class="mr-1" color="blue-grey darken-1">
                mdi-plus
              </v-icon>
              <p class="ma-0 blue-grey--text text--darken-1 body-2">
                {{ $t('assignee_selector.select_assignee') }}
              </p>
            </v-btn>
          </AssigneesSearchPopover>
        </v-col>
      </v-row>

      <v-row no-gutters class="mb-4">
        <v-col cols="6" data-tourid="tasks-creator-dueDate">
          <!-- This will be the new due date picker to replace DueDatePicker.vue someday -->
          <p class="ma-0 pa-0 mb-2 blue-grey--text text--darken-1 caption">
            {{ $t('common.due_date') }}
          </p>

          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <div v-show="dueDateText" v-on="on" class="d-flex">
                <p class="ma-0 pa-0 blue-grey--text text--darken-3 body-1">
                  {{ dueDateText }}
                </p>
                <v-icon
                  v-if="dueDateText"
                  color="blue-grey"
                  class="ms-2"
                  size="20"
                  @click.stop="handleChange('dueDate', null)"
                  >mdi-close-circle</v-icon
                >
              </div>
              <v-btn
                v-show="!dueDateText"
                v-on="on"
                rounded
                small
                class="blue-grey--text text--darken-1 body-2 mr-2"
                color="blue-grey lighten-3"
                elevation="0"
                data-tourid="dueDate-selector-trigger"
              >
                <v-icon size="16" class="mr-1" color="blue-grey darken-1">
                  mdi-plus
                </v-icon>
                <p class="ma-0 blue-grey--text text--darken-1 body-2">
                  {{ $t('common.select_due_date') }}
                </p>
              </v-btn>
            </template>
            <v-date-picker
              no-title
              reactive
              class="tasks-creator-due-date"
              :value="initialDueDate"
              @change="handleDueDateChange"
            />
          </v-menu>
        </v-col>
        <v-col cols="6" data-tourid="tasks-creator-priority">
          <!-- This will be the new PrioritySelector to replace PrioritySelector.vue someday -->
          <p class="ma-0 pa-0 mb-2 blue-grey--text text--darken-1 caption">
            {{ $t('common.priority') }}
          </p>
          <Menu :options="priorityOptions">
            <template v-slot:activator="{ on }">
              <div v-show="task.priority" v-on="on" class="d-flex">
                <Priority v-if="task.priority" :priority="task.priority" />
                <v-icon
                  v-if="task.priority"
                  color="blue-grey"
                  class="ms-2"
                  size="20"
                  @click.stop="handleChange('priority', null)"
                  >mdi-close-circle</v-icon
                >
              </div>

              <v-btn
                v-show="!task.priority"
                v-on="on"
                rounded
                small
                class="blue-grey--text text--darken-1 body-2 mr-2"
                color="blue-grey lighten-3"
                elevation="0"
                data-tourid="priority-selector-trigger"
              >
                <v-icon size="16" class="mr-1" color="blue-grey darken-1">
                  mdi-plus
                </v-icon>
                <p class="ma-0 blue-grey--text text--darken-1 body-2">
                  {{ $t('common.add_priority') }}
                </p>
              </v-btn>
            </template>
          </Menu>
        </v-col>
      </v-row>

      <v-row no-gutters class="mb-4">
        <TagsSelector
          data-tourid="tasks-creator-tags"
          :label="$t('common.tag_other')"
          :siteId="siteId"
          :value="selectedTags"
          @change="handleSelectTag"
        />
      </v-row>

      <div data-tourid="tasks-creator-description">
        <v-textarea
          hide-details
          outlined
          rows="4"
          class="font-size-14 pt-0"
          ref="taskTextArea"
          :maxlength="$constants.TASK.nameMaxLength"
          :label="$t('common.description')"
          :placeholder="$t('tasks.task_description')"
          :value="task.description"
          @input="handleChange('description', $event)"
        />
      </div>

      <div v-if="links.length > 0" class="mt-4">
        <p class="mb-2 pa-0 blue-grey--text caption">
          {{ $t('common.links') }}
        </p>

        <div class="d-flex flex-wrap">
          <LinkChip
            v-for="(link, index) in links"
            :key="link + index"
            :link="link"
          />
        </div>
      </div>

      <p class="mt-4 mb-2 pa-0 blue-grey--text caption">
        {{ $t('common.media') }}
      </p>

      <div
        class="pb-8 d-flex"
        :class="hasMediaOrAttachments ? 'flex-column' : ''"
      >
        <div :style="!hasMediaOrAttachments ? 'width: 100px' : 'width: 100%'">
          <MediaContainer
            data-tourid="tasks-creator-media"
            class="mr-4"
            onlyThumbnails
            :images="task.media"
            @change="handleMediaChange"
          />
        </div>

        <FileUploadContainer
          data-tourid="tasks-creator-attachments"
          :files="attachments"
          @fileSelected="handleFileSelected"
          @removeFile="handleRemoveFile"
        />
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import { utils } from 'frontend-js-shared'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import _ from 'lodash'
import * as moment from 'moment'
import config from '@/config'

import ChipAvatar from '@/components/ChipAvatar.vue'
import Menu from '@/components/UI/Menu.vue'
import EntityIcon from '@/components/EntityIcon.vue'
import AssigneesSearchPopover from '@/components/AssigneesSearchPopover/AssigneesSearchPopover.vue'
import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import FileUploadContainer from '../FileUploadContainer.vue'
import LinkChip from './LinkChip.vue'
import taskPermissions from '@/mixins/taskPermissions.vue'
import Button from '@/components/UI/Button.vue'
import getEntityId from '../../lib/getEntityId'
import Priority from '@/components/Priority.vue'
import PlannerIntegrationBadge from '@/components/PlannerIntegrationBadge.vue'
import TagsSelector from '@/components/TagsSelector/TagsSelector.vue'

const SORT_BY_COINCIDENCE = '_score'

export default {
  name: 'TaskDetail',

  mixins: [mixin, trackerMixin, taskPermissions],

  components: {
    ChipAvatar,
    AssigneesSearchPopover,
    MediaContainer,
    FileUploadContainer,
    EntityIcon,
    Button,
    Menu,
    LinkChip,
    Priority,
    PlannerIntegrationBadge,
    TagsSelector,
  },

  props: {
    task: {
      type: Object,
      default: () => {},
    },
    hasSubscribers: Boolean,
    attachments: {
      type: Array,
      default: () => [],
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },

  async mounted() {
    this.getLinkedEntity()
    this.identifyLinks()
    if (this.isPlannerBadgesEnabled) {
      this.getIntegrationsRelations()
    }
  },

  data() {
    return {
      priorityOptions: [
        {
          action: () => this.handleChange('priority', 'high'),
          id: 'high',
          title: this.$t('rev.priority.high'),
        },
        {
          action: () => this.handleChange('priority', 'medium'),
          id: 'medium',
          title: this.$t('rev.priority.medium'),
        },
        {
          action: () => this.handleChange('priority', 'low'),
          id: 'low',
          title: this.$t('rev.priority.low'),
        },
      ],
      isLoadingAccounts: false,
      isLoadingAttachment: false,
      isLoadingEntity: false,
      availableAccounts: [],
      links: [],
      entityName: '',
      statusOptions: [
        {
          id: this.$constants.tasksStatus.open,
          title: this.$t('tasks.open'),
          action: () =>
            this.handleChange('status', this.$constants.tasksStatus.open),
        },
        {
          id: this.$constants.tasksStatus.inProgress,
          title: this.$t('tasks.in_progress'),
          action: () =>
            this.handleChange('status', this.$constants.tasksStatus.inProgress),
        },
        {
          id: this.$constants.tasksStatus.completed,
          title: this.$t('tasks.complete'),
          action: () =>
            this.handleChange('status', this.$constants.tasksStatus.completed),
        },
      ],

      attachmentOptions: [
        {
          title: this.$t('common.image'),
          action: this.$emit('attachImage'),
          icon: 'mdi-image-outline ',
        },
        {
          title: this.$t('attachments.file'),
          action: this.$emit('attachFile'),
          icon: 'mdi-file-outline ',
        },
      ],
      entityNotFound: false,
      plannerIntegration: null,
      selectedTags: this.task?.tags || [],
    }
  },

  watch: {
    'task.description': _.debounce(function() {
      this.identifyLinks()
    }, 1000),

    'task.tags': {
      handler(newTags) {
        this.selectedTags = newTags || []
      },
      immediate: false,
    },
  },

  methods: {
    async searchAccounts(terms) {
      try {
        this.isLoadingAccounts = true

        const response = await api.account.getAccounts(this.organizationId, {
          filters: {
            isDeleted: false,
            site: terms ? undefined : this.siteId,
            status: 'joined',
          },
          terms,
          sort: {
            by: SORT_BY_COINCIDENCE,
            order: 'desc',
          },
        })

        this.availableAccounts = response?.data?.results ?? []
      } catch (err) {
        this.showError(err)
      } finally {
        this.isLoadingAccounts = false
      }
    },

    handleAssigneesChange(assignee) {
      this.$emit('changeTask', 'assignees', assignee.items)
    },

    handleStatusClick() {
      if (this.task?.status !== this.$constants.tasksStatus.completed) {
        return this.handleChange(
          'status',
          this.$constants.tasksStatus.completed
        )
      }
      this.handleChange('status', this.$constants.tasksStatus.open)
    },

    handleChange(taskProperty, value) {
      this.$emit('changeTask', taskProperty, value)
    },

    handleDueDateChange(date) {
      this.$emit('changeTask', 'dueDate', moment(date).toISOString())
    },

    handleMediaChange(media) {
      this.$emit('changeTask', 'media', media)
    },

    handleFileSelected(newFile) {
      this.$emit('updateAttachments', newFile)
    },

    handleRemoveFile(data) {
      this.$emit('removeAttachment', data)
    },

    handleSelectTag(value) {
      this.selectedTags = value.tags
      this.$emit('changeTask', 'tags', value.tags)
    },

    goToEntity() {
      if (this.entityNotFound) return
      this.trackTasks('tasks.goToEntity', {
        entityId: this.task.entityId,
        entityType: this.task.entityType,
      })
      this.$router.push(this.entityLink)
      this.$emit('close')
    },

    copyToClipboard() {
      const url = `${config.webAppURL}${this.$route.fullPath}`
      navigator.clipboard.writeText(url)
      this.showSuccess(this.$t('tasks.task_link_copied'))
      this.trackTasks('tasks.copyLink', {
        taskId: this.task?.id,
        from: 'editDrawer',
      })
    },

    async getLinkedEntity() {
      try {
        if (!this.hasLinkedEntity || this.task.entityName) return
        this.isLoadingEntity = true

        const entityTypesApi = {
          [this.$constants.revTypes.improvementIdea]: 'rev',
          [this.$constants.revTypes.quickAction]: 'quickaction',
          [this.$constants.revTypes.problemSolving]: 'problemsolving',
        }

        const { data } = await api[
          entityTypesApi[this.task.entityType]
        ].getById(this.organizationId, this.task.entityId)

        this.entityName = data?.name
        this.isLoadingEntity = false
      } catch (e) {
        if (e.response.status === 404) {
          this.entityName = this.$t('tasks.not_found')
          this.entityNotFound = true
        }
        this.isLoadingEntity = false
      }
    },

    identifyLinks() {
      const urlRegex = /(https?:\/\/[^\s]+)/g
      this.links = this.task?.description?.match?.(urlRegex) ?? []
    },

    async getIntegrationsRelations() {
      if (this.plannerIntegration || !this.task?.id) return
      const { data } = await api.integrations.getIntegrationsRelations({
        organizationId: this.organizationId,
        filters: { entityId: this.task?.id, externalPlatformName: 'planner' },
      })

      this.plannerIntegration = data?.results?.[0] ?? null
    },
  },

  computed: {
    ...mapGetters(['account', 'organizationId', 'siteId']),

    publicTaskReason() {
      if (!this.hasLinkedEntity && this.hasSubscribers)
        return this.$t('tasks.public_subscribers_tooltip')

      return this.$t('tasks.linked_task')
    },

    permissions() {
      return this.getTasksPermissions({ task: this.task })
    },

    hasAssignee() {
      return !_.isEmpty(this.task.assignees)
    },

    createdBy() {
      return this.isEditMode ? this.task?.createdBy ?? {} : this.account
    },

    assigneeId() {
      return this.task?.assignees?.[0]?.id ?? ''
    },

    assignee() {
      return this.task?.assignees?.[0] ?? {}
    },

    status() {
      return this.task?.status
    },

    statusText() {
      return (
        this.statusOptions?.find(status => status.id === this.task?.status)
          ?.title ?? this.$t('tasks.open')
      )
    },

    isCompleted() {
      return this.task?.status === this.$constants.tasksStatus.completed
    },

    statusIcon() {
      return this.isCompleted ? 'mdi-check-circle' : 'mdi-radiobox-blank'
    },

    dueDateText() {
      if (!this.task?.dueDate) return ''
      const date = moment(this.task?.dueDate).toISOString()
      return utils.dates.formatDate(date, 'MMM DD, YYYY')
    },

    name() {
      return this.task?.name ?? ''
    },

    initialDueDate() {
      if (!this.task.dueDate) return ''
      return moment(this.task?.dueDate).format('YYYY-MM-DD')
    },

    hasMediaOrAttachments() {
      return this.task?.media?.length > 0 || this.attachments?.length > 0
    },

    hasLinkedEntity() {
      return Boolean(this.task.entityId)
    },

    entityLink() {
      if (this.task.entityType === this.$constants.revTypes.improvementIdea)
        return `/rev/${this.organizationId}/${this.task.entityId}`
      if (this.task.entityType === this.$constants.revTypes.quickAction)
        return `/quick-action/${this.organizationId}/${this.task.entityId}`
      if (this.task.entityType === this.$constants.revTypes.problemSolving)
        return `/problem-solving/${this.organizationId}/${this.task.entityId}`
      return ''
    },

    privacyIcon() {
      return this.task?.isPrivate ? 'mdi-lock-outline' : 'mdi-earth'
    },

    privacyText() {
      if (this.hasLinkedEntity) return this.$t('tasks.public_task_text')
      return this.task?.isPrivate
        ? this.$t('tasks.private_task_text')
        : this.$t('tasks.public_task_text')
    },

    privacyButtonText() {
      return this.task?.isPrivate
        ? this.$t('tasks.make_public')
        : this.$t('tasks.make_private')
    },

    isAssigneeOtherSite() {
      if (!this.hasAssignee) return false
      return (
        getEntityId(this.task.assignees[0].site) !=
        getEntityId(this.account.site)
      )
    },

    isPlannerBadgesEnabled() {
      return this.$isFeatureEnabled(this.$featuresKeys.plannerBadges)
    },
  },
}
</script>

<style lang="scss">
.fixed-header {
  position: absolute;
  width: 100%;
  top: 0;
  z-index: 2;
  background: $color-white;
  border-bottom: 1px solid $color-blue-grey-lighten2;
}

.rounded-top {
  border-top-left-radius: 8px !important;
  border-top-right-radius: 8px !important;
}

.task-status-class {
  .v-input__control .v-select__slot {
    .v-select__selections:has(span) {
      input {
        width: 0px !important;
        min-width: 0px !important;
      }
    }
    width: auto !important;
  }
}

.bubble {
  &:hover {
    box-shadow: inset 18px 18px 18px 18px $color-blue-grey-lighten2 !important;
    border-radius: 50%;
  }
  width: 30px;
  height: 30px;
}
.entity-chip {
  max-width: 350px;
  &:hover .caption {
    text-decoration: underline;
  }
}
</style>
